import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  formatAmount,
  formatBuyerAddress,
  formatDate,
  stringToUpperCase,
} from "../../../../components/Helper";
import { SelfDocuments } from "../../../../components/Enums";

const fontStyles = StyleSheet.create({
  TextCenter: {
    textAlign: "center",
  },
  boldText: {
    fontWeight: "bold",
  },
  fontXL: {
    fontSize: 12,
  },
  fontLg: {
    fontSize: 10,
  },
  fontMd: {
    fontSize: 8.4,
  },
  fontSm: { fontSize: 7.4 },
});

export const styles = StyleSheet.create({
  ...fontStyles,
  page: {
    paddingHorizontal: 30,
    paddingVertical: 15,
    fontFamily: "HostGrotesk",
    backgroundColor: "white",
    ...fontStyles.fontMd,
  },
  header: {
    marginTop: 5,
    marginBottom: 10,
    ...fontStyles.fontXL,
  },
  table: {
    borderLeft: 1,
    width: "100%",
  },
  tableRow: {
    width: "100%",
    flexDirection: "row",
    borderBottom: 1,
  },
  tableCell: {
    paddingVertical: 1,
    paddingHorizontal: 3,
    borderRight: 1,
  },
  tableCell1: {
    width: "40%",
    paddingVertical: 1,
    paddingHorizontal: 3,
    borderRight: 1,
  },
  tableCell2: {
    width: "60%",
    paddingVertical: 1,
    paddingHorizontal: 3,
    borderRight: 1,
  },
  flex: { flexDirection: "row" },
});

const FumigationCertificateInvoice = ({
  salesInvoiceData,
  settingsData,
}: any) => {
  const documentInputFields =
    salesInvoiceData?.my_documents_inputs?.[
      SelfDocuments.FUMIGATION_CERTIFICATE
    ];

  const phytoCertificateInputFields =
    salesInvoiceData?.my_documents_inputs?.[
      SelfDocuments.PHYTOSANITARY_CERTIFICATE
    ];

  const sealNumbers = salesInvoiceData?.containers
    ?.map((c: any) => `${c?.self_seal?.seal_no || "-"}`)
    .join(", ");

  const productNames = salesInvoiceData?.sales_invoice_products
    ?.map((item: any) => stringToUpperCase(item?.product?.name))
    .join(" , ");

  const containerSummary = salesInvoiceData?.containers?.reduce(
    (acc: any, c: any) => {
      acc[c.size] = (acc[c.size] || 0) + 1;
      return acc;
    },
    {},
  );
  const containerSummaryText = Object.entries(containerSummary || {})
    .map(([size, count]) => `${count} X ${size}'`)
    .join(" + ");

  const packingDescriptions = salesInvoiceData?.sales_invoice_products
    ?.map((p: any) => {
      const packingName = p?.packing_type?.name
        ? stringToUpperCase(p.packing_type.name)
        : "BAGS";

      const net = p?.packing_net_weight
        ? ` NET ${formatAmount(p.packing_net_weight, 3)} KGS`
        : "";

      const gross = p?.packing_gross_weight
        ? ` & GROSS ${formatAmount(p.packing_gross_weight, 3)} KGS EACH BAG`
        : "";

      return `(PACKED IN ${packingName})${net}${gross}.`;
    })
    .join("\n");

  return (
    <Document>
      <Page style={styles.page} size="A4">
        {/* Header */}
        <View style={[styles.TextCenter, styles.boldText, styles.header]}>
          <View style={{ minHeight: 10 }}></View>
          <Text>FUMIGATION CERTIFICATE</Text>
        </View>

        <View style={[styles.table, { borderTop: 1 }]}>
          <View style={[styles.tableRow]}>
            <View style={[styles.tableCell1, { width: "40%" }]}>
              <Text style={[styles.boldText]}>Dte. Of PPQS Regd .No.:</Text>
              <Text>
                {stringToUpperCase(documentInputFields?.registration_no)}
              </Text>
            </View>
            <View style={[styles.tableCell2]}>
              <Text style={[styles.boldText]}>
                Treatment Certificate Number:
              </Text>
              <Text>
                {stringToUpperCase(
                  documentInputFields?.treatment_certificate_no,
                )}
              </Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <View style={[styles.tableCell1, styles.flex, { padding: 0 }]}>
              <Text style={[styles.tableCell, styles.boldText]}>Date</Text>
              <Text style={[styles.tableCell, { flexGrow: 1, borderRight: 0 }]}>
                {formatDate(documentInputFields?.date)}
              </Text>
            </View>
            <View style={[styles.tableCell2, styles.flex, { padding: 0 }]}>
              <Text style={[styles.tableCell, styles.boldText]}>
                Date of Issue
              </Text>
              <Text style={[styles.tableCell, { flexGrow: 1, borderRight: 0 }]}>
                {formatDate(phytoCertificateInputFields?.date_of_issue)}
              </Text>
            </View>
          </View>

          <Text style={[styles.tableRow, styles.tableCell]}>
            This is to certify that the goods described below were treated in
            accordance with the fumigation treatment requirements of importing
            country
            <Text style={[styles.boldText]}>
              {" "}
              (
              {stringToUpperCase(documentInputFields?.destination_port_country)}
              ){" "}
            </Text>
            and declared that the consignment has been verified free of
            impervious surfaces/layers such as plastic wrapping or laminated
            plastic films, lacquered or painted surfaces, aluminium foil,tarred
            or waxed paper etc. that may adversely affect the penetration of the
            fumigant, prior to fumigation.
          </Text>
        </View>

        {/* Details of Treatment */}
        <View style={[styles.table]}>
          {/*  HEADER */}
          <View style={[styles.tableRow, styles.tableCell]}>
            <Text style={[styles.boldText, styles.fontLg]}>
              Details of Treatment
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Name of Fumigant</Text>
            <Text style={[styles.tableCell2]}>
              {stringToUpperCase(phytoCertificateInputFields?.chemical)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Date of Fumigation</Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              {formatDate(phytoCertificateInputFields?.date_of_treatment)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Place of Fumigation</Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              {stringToUpperCase(documentInputFields?.place_of_fumigation)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Dosage of Fumigant (Gm/m³)</Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              {stringToUpperCase(phytoCertificateInputFields?.concentration)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Duration of Fumigation (hours)
            </Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              {stringToUpperCase(phytoCertificateInputFields?.duration)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Average Ambient Temperature During Fumigation (°C)
            </Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              {stringToUpperCase(phytoCertificateInputFields?.temperature)}
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Fumigation performed under gastight sheets
            </Text>
            <Text style={[styles.tableCell2, styles.boldText]}>
              YES/
              <Text style={{ textDecoration: "line-through" }}>NO</Text>
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <View style={[styles.tableCell1]}>
              <Text>
                If containers are not fumigated under gas-tight sheets,pressure
                decay value (from 200-100 Pascal's) in seconds.
              </Text>
            </View>
            <View style={[styles.tableCell2, styles.boldText]}>
              <Text>N.A</Text>
            </View>
          </View>
        </View>

        {/* Description of Goods */}
        <View style={[styles.table]}>
          {/*  HEADER */}
          <View style={[styles.tableRow, styles.tableCell]}>
            <Text style={[styles.boldText, styles.fontLg]}>
              Description of Goods
            </Text>
          </View>
          <View style={[styles.tableRow]}>
            <View style={[styles.tableCell1]}>
              <Text>Container Number (or numerical link)</Text>
              <Text>/ Seal Number</Text>
            </View>
            <View style={[styles.tableCell2]}>
              <Text style={[styles.boldText]}>
                SAID TO HAVE BEEN LOADED IN [{containerSummaryText}]
              </Text>
              <Text>{sealNumbers}</Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Name & Address of Exporter</Text>
            <View style={[styles.tableCell2]}>
              <Text>{settingsData.company_name}</Text>
              <Text>{settingsData.company_address} </Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Name & Address of Importer</Text>
            <View style={[styles.tableCell2]}>
              <Text>{stringToUpperCase(salesInvoiceData?.buyer.name)}</Text>
              <Text>{formatBuyerAddress(salesInvoiceData?.buyer, true)}</Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Notify Party</Text>
            <Text style={[styles.tableCell2]}>
              {salesInvoiceData?.is_notify_party
                ? stringToUpperCase(salesInvoiceData.notify_party.name)
                : "SAME AS IMPORTER"}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Type and description of cargo
            </Text>
            <Text style={[styles.tableCell2]}>
              {stringToUpperCase(productNames)}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <View style={[styles.tableCell1]}>
              <Text>Quantity (MTs)/No. of packages/No.</Text>
              <Text>pieces</Text>
            </View>
            <View style={[styles.tableCell2]}>
              <Text>
                {salesInvoiceData?.total_gross_weight &&
                  `GROSS. WT.: ${formatAmount(
                    salesInvoiceData?.total_gross_weight / 1000,
                    3,
                  )} MTS`}
                {salesInvoiceData?.total_net_weight &&
                  `, NET. WT.: ${formatAmount(
                    salesInvoiceData?.total_net_weight / 1000,
                    3,
                  )} MTS`}
              </Text>
              <Text>
                {salesInvoiceData?.total_no_of_bags &&
                  `(TOTAL ${salesInvoiceData?.total_no_of_bags} BAGS)`}
              </Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Description of packaging material
            </Text>
            <Text style={[styles.tableCell2]}>{packingDescriptions}</Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Shipping mark or brand</Text>
            <Text style={[styles.tableCell2]}>N/M</Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Name of the Vessel / Ship</Text>
            <Text style={[styles.tableCell2]}>
              {stringToUpperCase(salesInvoiceData?.vessel_no)}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Port & Country of Loading</Text>
            <Text style={[styles.tableCell2]}>
              {stringToUpperCase(salesInvoiceData?.origin_port?.name)},{" "}
              {stringToUpperCase(salesInvoiceData?.origin_port?.country)}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Decleared Port of Entry</Text>
            <Text style={[styles.tableCell2]}>
              {salesInvoiceData?.destination_port
                ? stringToUpperCase(salesInvoiceData?.destination_port)
                : "-"}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1, styles.boldText]}>
              Additional Declaration
            </Text>
            <Text style={[styles.tableCell2]}>
              INVOICE NO. {salesInvoiceData.invoice_no} DATE:{" "}
              {formatDate(salesInvoiceData?.invoice_date)}
            </Text>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>
              Name & Signature of accredited Fumigation Operator with seal &
              date/Accreditation Number
            </Text>
            <View style={[styles.tableCell2, { minHeight: 150 }]}>
              <Text></Text>
            </View>
          </View>

          <View style={[styles.tableRow]}>
            <Text style={[styles.tableCell1]}>Date&Place</Text>
            <View style={[styles.tableCell, { paddingHorizontal: 45 }]}>
              <Text></Text>
            </View>
            <View style={[styles.tableCell, { flexGrow: 1 }]}>
              <Text></Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <Text style={[styles.fontSm]}>
          *No financial liability attaches to or is assumed by the certifying
          company, its partners, directors of representatices in respect to this
          certificate.
        </Text>
      </Page>
    </Document>
  );
};

export default FumigationCertificateInvoice;
